import { useCallback, useRef, useState } from "react";
import ScrapPhotoUploadTab from "./ScrapPhotoUploadTab";
import ScrapWeightUploadTab from "./ScrapWeightUploadTab";
import { TabProps } from "./lib";

type SubView = "weight" | "photo";

/**
 * The SCRAP tab is really two independent contract lists behind one label:
 * Scrap Weight upload (left) and Scrap Photo upload (right). Only the visible
 * one's counts reach the outer day-strip badge — the hidden one still
 * refreshes quietly in the background, the same as every other tab does, but
 * its counts are thrown away rather than fighting the visible one for the
 * badge.
 *
 * onSynced is a different story: the app's SYNC button counts exactly one
 * "done" per tab in the top bar, and SCRAP is one tab in that bar even
 * though it now runs two independent fetches. Both sub-tabs' onSynced calls
 * are collected here and only forwarded ONCE — otherwise the outer counter
 * gets decremented twice for one slot and the button can flip back to
 * "SYNC" before the still-loading sub-tab (or an entirely different tab)
 * has actually finished.
 */
export default function ScrapTab(props: TabProps) {
  const [subView, setSubView] = useState<SubView>("weight");
  const noop = useCallback(() => {}, []);

  const pending = useRef<{ token: number; weight: boolean; photo: boolean; allFresh: boolean }>({
    token: -1,
    weight: false,
    photo: false,
    allFresh: true,
  });

  const reportSynced = useCallback(
    (which: "weight" | "photo", isFresh: boolean = true) => {
      const state = pending.current;
      if (state.token !== props.refreshToken) {
        state.token = props.refreshToken;
        state.weight = false;
        state.photo = false;
        state.allFresh = true;
      }
      state[which] = true;
      if (!isFresh) state.allFresh = false;
      if (state.weight && state.photo) props.onSynced(state.allFresh);
    },
    [props.refreshToken, props.onSynced],
  );

  const onWeightSynced = useCallback(
    (isFresh?: boolean) => reportSynced("weight", isFresh),
    [reportSynced],
  );
  const onPhotoSynced = useCallback(
    (isFresh?: boolean) => reportSynced("photo", isFresh),
    [reportSynced],
  );

  return (
    <div className="scrap-outer">
      <nav className="view-switch" aria-label="Scrap views">
        <button
          className={subView === "weight" ? "is-active" : ""}
          type="button"
          onClick={() => setSubView("weight")}
        >
          SCRAP WEIGHT UPLOAD
        </button>
        <button
          className={subView === "photo" ? "is-active" : ""}
          type="button"
          onClick={() => setSubView("photo")}
        >
          SCRAP PHOTO UPLOAD
        </button>
      </nav>

      <div hidden={subView !== "weight"}>
        <ScrapWeightUploadTab
          {...props}
          isActive={props.isActive && subView === "weight"}
          onCounts={subView === "weight" ? props.onCounts : noop}
          onSynced={onWeightSynced}
        />
      </div>
      <div hidden={subView !== "photo"}>
        <ScrapPhotoUploadTab
          {...props}
          isActive={props.isActive && subView === "photo"}
          onCounts={subView === "photo" ? props.onCounts : noop}
          onSynced={onPhotoSynced}
        />
      </div>
    </div>
  );
}
